<template>
  <section class="advice">
    <div class="container advice__inner">
      <div class="advice__content">
        <h2 class="advice__title">
          <span class="advice__title-orange">{{ t('advice.titleOrange') }}</span
          ><br />
          {{ t('advice.titleWhite') }}
        </h2>
        <p class="advice__text">{{ t('advice.desc1') }}</p>
        <p class="advice__text">{{ t('advice.desc2') }}</p>
        <p class="advice__text">{{ t('advice.desc3') }}</p>
        <NuxtLink
          to="/nos-conseils"
          class="btn advice__cta">
          {{ t('advice.cta') }}
        </NuxtLink>
      </div>

      <div
        v-if="backArticle || frontArticle"
        class="advice__visual">
        <ShowcaseArticleCard
          v-if="backArticle"
          :article="backArticle"
          variant="back" />
        <ShowcaseArticleCard
          v-if="frontArticle"
          :article="frontArticle"
          variant="front" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { ComputedRef, PropType } from 'vue'
import type { ShowcaseArticleCardData } from '~/types/homeShowcases'
import ShowcaseArticleCard from '~/components/cards/ShowcaseArticleCard.vue'

const props = defineProps({
  articles: {
    type: Array as PropType<readonly ShowcaseArticleCardData[]>,
    default: () => [],
  },
})

const { t } = useI18n()

const backArticle: ComputedRef<ShowcaseArticleCardData | undefined> = computed(
  (): ShowcaseArticleCardData | undefined => props.articles[0],
)

const frontArticle: ComputedRef<ShowcaseArticleCardData | undefined> = computed(
  (): ShowcaseArticleCardData | undefined => props.articles[1],
)
</script>

<style scoped>
.advice {
  background: var(--bg-base);
  padding-block: var(--space-20);
  overflow: hidden;
}

.advice__inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-16);
  align-items: center;
}

.advice__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.advice__title {
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 800;
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.advice__title-orange {
  color: var(--accent-orange);
}

.advice__text {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

.advice__cta {
  width: fit-content;
  background: var(--accent-orange);
  color: #fff;
  font-weight: 700;
  font-size: var(--text-base);
  padding: 0.75em 2em;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: filter var(--duration-fast) var(--ease-out);
  box-shadow: var(--shadow-orange);
  margin-top: var(--space-2);
}

.advice__cta:hover {
  filter: brightness(1.1);
}

.advice__visual {
  position: relative;
  height: 420px;
}

@media (max-width: 900px) {
  .advice__inner {
    grid-template-columns: 1fr;
    gap: var(--space-10);
  }

  .advice__visual {
    height: 340px;
    max-width: 420px;
    margin-inline: auto;
    width: 100%;
  }

  .advice__title {
    font-size: clamp(1.5rem, 6vw, 2rem);
  }

  .advice__cta {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .advice__visual {
    height: 300px;
  }
}
</style>
